import {
  TBoardViewMode,
  TDate,
  TREQUEST_LlBoardListing,
  TRESPONSE_GET_OPTIMAL_PLAN_BY_OPL_IDS,
  TRESPONSE_LLBoardListing,
  TTimelineDateRangeDTO,
  TWithoutPlanVersion,
} from '@monorepo/types';
import { getAvailableDateRangeInTimeline, getTotalDaysInTimeline, LL_MODEL, TLLTimelineModel } from '@monorepo/ui';
import { DateUtils } from '@monorepo/utils';

type TOplPlan = TRESPONSE_GET_OPTIMAL_PLAN_BY_OPL_IDS['opl_plans'][number];
type TBoardLine = TLLTimelineModel['entities'][number]['entities'][number];
type TBoardElement = TBoardLine['elements'][number];

// One proposed strip: a row in the table and a dotted strip on the board, matched by id.
export type TAutoPlanSuggestion = {
  id: string;
  otlId: string;
  oplId: string;
  factoryId: string;
  factoryName: string;
  productType: string;
  lineSubject: string;
  lineName: string;
  startDate: TDate;
  endDate: TDate;
  qty: number;
};

// Must match ORGANIZATION_HIERARCHY_FILTER_ID on the backend; leaf nodes of this filter are line subjects.
const ORG_HIERARCHY_FILTER_KEY = 'FACTORY_LOCATION_HIERARCHY';
const PROPOSED_STRIP = { color: '#FFF4E5', borderColor: '#B26A00', textColor: '#8A5300', heightInPercent: 70 };
// Same visible days as the LL Board.
const DAYS_TO_SHOW = { WEEKLY: 30, DAILY: 7 };
const VIEW_STEP_DAYS = { WEEKLY: 7, DAILY: 4 };
const VIEW_LEAD_DAYS = 2;

const isDailyOrWeekly = (viewType: TBoardViewMode): viewType is 'DAILY' | 'WEEKLY' =>
  viewType === 'DAILY' || viewType === 'WEEKLY';

const createProposedStrip = (suggestion: TAutoPlanSuggestion, isFocused: boolean): TBoardElement => ({
  type: 'STRIP',
  id: suggestion.id,
  name: `${suggestion.oplId} (Proposed)`,
  // Date-only ends are drawn to the end of that day, same as day-end strips on the LL Board.
  range: { startDate: suggestion.startDate, endDate: suggestion.endDate },
  stackCount: 0,
  heightInPercent: PROPOSED_STRIP.heightInPercent,
  color: PROPOSED_STRIP.color,
  mask: false,
  textBorderColor: PROPOSED_STRIP.borderColor,
  isTextBorderDashed: true,
  isStripHighlighted: isFocused,
  textList: [{ value: `${suggestion.oplId} (${suggestion.qty})`, color: PROPOSED_STRIP.textColor }],
  iconList: [],
  additionalInfo: `Proposed by Auto Plan </br> OTL: ${suggestion.otlId} </br> Qty: ${suggestion.qty}`,
});

export const AUTO_PLAN_BOARD_MODEL = {
  // Kept in the planner's order (OPL delivery date), so "Suggestion 3" is the third row of the table.
  getSuggestions: (oplPlans: TOplPlan[]): TAutoPlanSuggestion[] =>
    oplPlans.flatMap((oplPlan) =>
      oplPlan.plans.map((plan, index) => ({
        id: `proposed_${oplPlan.opl_id}_${oplPlan.factory_id}_${index}`,
        otlId: oplPlan.otl_id,
        oplId: oplPlan.opl_id,
        factoryId: oplPlan.factory_id,
        factoryName: oplPlan.factory_name,
        productType: oplPlan.product_type,
        lineSubject: plan.selected_line_subject,
        lineName: plan.selected_line_display_name,
        startDate: plan.load_start_date as TDate,
        endDate: plan.load_end_date as TDate,
        qty: plan.to_be_planned_qty,
      })),
    ),

  // Board of only the suggested lines, loaded from just before the focused suggestion so it is always in the window.
  createRequest: (
    suggestions: TAutoPlanSuggestion[],
    boardViewType: TBoardViewMode,
    focused: TAutoPlanSuggestion,
  ): TWithoutPlanVersion<TREQUEST_LlBoardListing> => {
    const lines = new Map(suggestions.map((suggestion) => [suggestion.lineSubject, suggestion.lineName]));
    return {
      boardView: {
        boardViewType,
        offset: 'DEFAULT',
        customViewMode: 'DEFAULT',
        dateRange: {
          from: DateUtils.subtractFromDate(focused.startDate, 'days', VIEW_LEAD_DAYS),
          to: focused.endDate,
        },
      },
      selectedFilters: [
        {
          key: ORG_HIERARCHY_FILTER_KEY,
          displayName: 'Org Hierarchy',
          type: 'ORG_HIERARCHY_FILTER',
          values: [...lines.entries()].map(([subject, displayName]) => ({ id: subject, value: displayName })),
        },
      ],
      pageNumber: 1,
      pageSize: lines.size,
    };
  },

  // The suggested lines with their existing strips and the suggestions drawn over them, the focused one highlighted.
  // Strips that Confirm would shift are shown where they are today.
  createModel: (
    llBoard: TRESPONSE_LLBoardListing,
    suggestions: TAutoPlanSuggestion[],
    focusedId: string | undefined,
  ): TLLTimelineModel => {
    const boardModel = LL_MODEL.createModel(llBoard);
    const proposedStripsByLine = new Map<string, TBoardElement[]>();
    suggestions.forEach((suggestion) =>
      proposedStripsByLine.set(suggestion.lineSubject, [
        ...(proposedStripsByLine.get(suggestion.lineSubject) ?? []),
        createProposedStrip(suggestion, suggestion.id === focusedId),
      ]),
    );

    const entities = boardModel.entities
      .map((factory) => ({
        ...factory,
        entities: factory.entities
          .filter((line) => line.data.type === 'OPL_LINE' && proposedStripsByLine.has(line.id))
          .map(
            (line): TBoardLine => ({
              ...line,
              name: { ...line.name, isClickable: false },
              elements: [...line.elements, ...(proposedStripsByLine.get(line.id) ?? [])],
            }),
          ),
      }))
      .filter((factory) => factory.entities.length > 0);

    return {
      ...boardModel,
      entities,
      comptedEntities: {
        totalEntities: entities.reduce((total, factory) => total + factory.entities.length, 0),
        totalGroupEntities: entities.length,
      },
    };
  },

  // Days to show around the suggestion, clamped to the loaded board; isInBoard is false when the board misses it.
  // The board never loads past the LL Board's own limit (6 months ahead by default), so a refetch can still miss it.
  getFocusedView: (
    boardModel: TLLTimelineModel,
    suggestion: TAutoPlanSuggestion,
  ): { viewDateRange: TTimelineDateRangeDTO; maxDaysToShow: number; isInBoard: boolean } => {
    const available = getAvailableDateRangeInTimeline(boardModel);
    // Both are YYYY-MM-DD, so string order is date order.
    const isInBoard = suggestion.startDate >= available.from && suggestion.startDate <= available.to;

    const viewType = boardModel.boardView.boardViewType;
    if (!isDailyOrWeekly(viewType))
      return { viewDateRange: available, maxDaysToShow: getTotalDaysInTimeline(boardModel), isInBoard };

    const from = DateUtils.subtractFromDate(suggestion.startDate, 'days', VIEW_LEAD_DAYS);
    const to = DateUtils.addToDate(from, 'days', DAYS_TO_SHOW[viewType] - 1);
    return {
      viewDateRange: DateUtils.fitDateRange({ from, to }, available, from),
      maxDaysToShow: DAYS_TO_SHOW[viewType],
      isInBoard,
    };
  },

  // Prev / next move the visible days inside the loaded board, a week at a time like the LL Board.
  shiftViewDateRange: (
    boardModel: TLLTimelineModel,
    current: TTimelineDateRangeDTO,
    direction: 'NEXT' | 'PREV',
  ): TTimelineDateRangeDTO => {
    const viewType = boardModel.boardView.boardViewType;
    const currentDays = DateUtils.dateRangeDiff(current, 'days') + 1;
    const step =
      isDailyOrWeekly(viewType) && currentDays >= DAYS_TO_SHOW[viewType]
        ? VIEW_STEP_DAYS[viewType]
        : Math.ceil(currentDays / 2);
    const sign = direction === 'NEXT' ? 1 : -1;
    const shifted = {
      from: DateUtils.addToDate(current.from, 'days', sign * step),
      to: DateUtils.addToDate(current.to, 'days', sign * step),
    };
    return DateUtils.fitDateRange(shifted, getAvailableDateRangeInTimeline(boardModel));
  },

  todayViewDateRange: (boardModel: TLLTimelineModel, current: TTimelineDateRangeDTO): TTimelineDateRangeDTO => {
    const today = DateUtils.getCrntDate();
    const currentDays = DateUtils.dateRangeDiff(current, 'days') + 1;
    const desired = { from: today, to: DateUtils.addToDate(today, 'days', currentDays - 1) };
    return DateUtils.fitDateRange(desired, getAvailableDateRangeInTimeline(boardModel), today);
  },
};
